<template>
  <div id="container"></div>
</template>

<script>
import { mapGetters } from 'vuex';
import G6 from '@antv/g6';
import { debounce } from '@/utils';
import flowChartData from './flowChartData';

export default {
  name: "HomePage",
  data() {
    return {
      graph: undefined,
    };
  },
  mounted() {
    this.initResizeListener();
    this.$nextTick(() => {
      this.drawFlowChart();
    });
  },
  computed: {
    ...mapGetters([
      'userInfo',
    ]),
  },
  beforeDestroy() {
    if (this.graph) {
      this.graph.destroy();
      this.graph = null;
    }
    window.removeEventListener('resize', this.__resizeHandler);
  },
  methods: {
    initResizeListener() {
      this.__resizeHandler = debounce(() => {
        if (this.graph) {
          this.graph.destroy();
          this.drawFlowChart();
        }
      }, 100);
      window.addEventListener('resize', this.__resizeHandler)
    },
    drawFlowChart() {
      G6.registerNode(
        'head-sql', {
          drawShape: function drawShape(cfg, group) {
            const width = cfg.style.width;
            const fill = cfg.style.fill;
            const rect = group.addShape('circle', {
              attrs: {
                x: 0,
                y: 50,
                width,
                height: 30,
                r: 20,
                fill: cfg.color || fill,
              },
              name: 'circle-shape',
            });
            group.addShape('text', {
              attrs: {
                text: cfg.name,
                x: 0,
                y: 0,
                fontSize: 45,
                textAlign: 'center',
                textBaseline: 'middle',
                fontWeight: 'bold',
                fill: cfg.color || fill,
              }
            });
            return rect;
          },
        },
        'single-node',
      );
      G6.registerNode(
        'tail-sql', {
          drawShape: function drawShape(cfg, group) {
            const width = cfg.style.width;
            const fill = cfg.style.fill;
            const rect = group.addShape('circle', {
              attrs: {
                x: 0,
                y: 0,
                width,
                height: 30,
                r: 20,
                fill: cfg.color || fill,
              },
              name: 'circle-shape',
            });
            group.addShape('text', {
              attrs: {
                text: cfg.name,
                x: 0,
                y: 50,
                fontSize: 45,
                textAlign: 'center',
                textBaseline: 'middle',
                fontWeight: 'bold',
                fill: cfg.color || fill,
              }
            });
            return rect;
          },
        },
        'single-node',
      );
      G6.registerNode(
        'sql',
        {
          drawShape: function drawShape(cfg, group) {
            const width = cfg.style.width;
            const fill = cfg.style.fill;
            const rect = group.addShape('circle', {
              attrs: {
                x: 0,
                y: 0,
                width,
                height: 30,
                r: width / 2,
                fill: cfg.color || fill,
                cursor: cfg.router ? 'pointer' : 'default',
              },
              name: 'circle-shape',
            });
            group.addShape('text', {
              attrs: {
                text: cfg.id,
                x: 0,
                y: 0,
                fill: '#FFFFFF',
                fontSize: 60,
                textAlign: 'center',
                textBaseline: 'middle',
                fontWeight: 'bold',
                cursor: cfg.router ? 'pointer' : 'default',
              }
            });
            const isLabelsInLeft = Number.parseInt(cfg.id, 10) % 2 === 0;
            const lineWidth = width * 5;
            group.addShape('rect', {
              attrs: {
                x: isLabelsInLeft ? - width / 2 - lineWidth : width / 2,
                y: 0,
                width: lineWidth,
                height: 10,
                fill: cfg.color || fill,
              },
            });
            group.addShape('text', {
              attrs: {
                text: cfg.name,
                x: isLabelsInLeft ? - (width / 2 + lineWidth) / 2 : ( width / 2 + lineWidth) / 2,
                y: -30,
                fill: cfg.color || fill,
                fontSize: 40,
                textAlign: 'center',
                textBaseline: 'middle',
                fontWeight: 'bold',
                cursor: cfg.router ? 'pointer' : 'default',
              }
            });
            group.addShape('circle', {
              attrs: {
                x: isLabelsInLeft ? - (width / 2 + lineWidth) : width / 2 + lineWidth,
                y: 5,
                width,
                height: 30,
                r: width / 5,
                fill: cfg.color || fill,
                cursor: cfg.router ? 'pointer' : 'default',
              },
              name: 'circle-shape',
            });
            return rect;
          },
          update: function update(cfg, item) {
            const group = item.getContainer();
            const children = group.get('children');
            const node = children[0];
            const circleLeft = children[1];
            const circleRight = children[2];

            const stroke = cfg.style.stroke;

            if (stroke) {
              node.attr('stroke', stroke);
            }
          },
        },
        'single-node',
      );

      G6.registerEdge('line', {
        itemType: 'edge',
        draw: function draw(cfg, group) {
          const startPoint = cfg.startPoint;
          const endPoint = cfg.endPoint;
          let path = [
            ['M', startPoint.x, startPoint.y],
            ['L', endPoint.x, endPoint.y],
          ];

          const line = group.addShape('path', {
            attrs: {
              path,
              stroke: cfg.data && cfg.data.color || '#909299',
              lineWidth: 6,
              endArrow: false,
            },
            name: 'path-shape',
          });
          return line;
        },
      });

      const width = document.getElementById('container').scrollWidth;
      const height = document.getElementById('container').scrollHeight || 500;
      const graph = new G6.Graph({
        container: 'container',
        width,
        height,
        layout: {
          type: 'dagre',
        },
        defaultNode: {
          type: 'sql',
          labelCfg: {
            style: {
              fill: '#000000A6',
              fontSize: 10,
            },
          },
          style: {
            width: 120,
            fill: '#909299',
          },
        },
        defaultEdge: {
          type: 'line',
        },
        fitView: true
      });

      graph.data(flowChartData);
      graph.render();
      graph.on('node:click', (event) => {
        const {
          userInfo,
        } = this;
        if (userInfo && userInfo.roles && userInfo.roles[0] && userInfo.roles[0].name === 'printer') { 
          return;
        }
        const item = event.item;
        const id = item._cfg && item._cfg.id;
        for (let row of flowChartData.nodes) {
          if (row.id === id && row.router) {
            this.$router.push({
              path: row.router
            });
            break;
          }
        }
      });
      this.graph = graph;
    }
  }
}
</script>

<style scoped>
  #container {
    height: calc(100% - 36px);
    box-sizing: border-box;
  }
</style>
